import { useEffect, useRef, useState } from 'react';
import { Blinds } from 'lucide-react';
import { useCardStyles } from './cardStyles';
import { attrNumber, humanState, useEntity, useEntityFriendlyName } from './useEntity';
import { useHAService } from '../../context/HAConnectionContext';

export interface CoverCardProps {
  entityId: string;
  title?: string;
}

export function CoverCard({ entityId, title }: CoverCardProps): JSX.Element {
  const styles = useCardStyles();
  const entity = useEntity(entityId);
  const name = useEntityFriendlyName(entityId, title);
  const { callService } = useHAService();
  const s = humanState(entity);
  const state = entity?.state ?? 'unknown';
  const isOpen = state === 'open' || state === 'opening';
  const positionAttr = attrNumber(entity, 'current_position');
  const [optimisticPosition, setOptimisticPosition] = useState<number | null>(null);
  const position = optimisticPosition ?? positionAttr ?? (state === 'open' ? 100 : 0);
  const clamped = Math.min(100, Math.max(0, Math.round(position)));

  // Drop optimistic override once HA confirms the new position, so the
  // displayed value tracks the real device state instead of drifting.
  useEffect(() => {
    if (optimisticPosition !== null && positionAttr !== undefined && Math.abs(positionAttr - optimisticPosition) <= 1) {
      setOptimisticPosition(null);
    }
  }, [positionAttr, optimisticPosition]);

  // Debounce set_cover_position so dragging the slider does not spam HA.
  // 250ms is short enough to feel instant, long enough to coalesce a drag.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const commitPosition = (pos: number) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      callService('cover', 'set_cover_position', { entity_id: entityId, position: pos }).catch(
        (err) => {
          console.error('[CoverCard] set_cover_position failed', entityId, err);
          setOptimisticPosition(null);
        },
      );
    }, 250);
  };

  const onSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pos = Number(e.target.value);
    setOptimisticPosition(pos);
    commitPosition(pos);
  };

  return (
    <div
      className={styles.container}
      data-domain="cover"
      data-entity-id={entityId}
      data-state={state}
    >
      <div className="flex items-center justify-between">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
            isOpen
              ? 'bg-sky-500/20 text-sky-500 dark:text-sky-400'
              : styles.isDark
              ? 'bg-gray-700/60 text-gray-500'
              : 'bg-gray-100 text-gray-400'
          }`}
        >
          <Blinds className="h-5 w-5" />
        </div>
        <span
          className={`text-xs uppercase tracking-wider ${
            isOpen ? 'text-sky-500 dark:text-sky-400' : styles.subtle
          }`}
        >
          {s.text}
        </span>
      </div>

      <div>
        <div className={`truncate text-sm font-semibold ${styles.title}`}>{name}</div>
        <div className={`text-xs ${styles.subtle}`}>
          Position <span className="tabular-nums">{clamped}%</span>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={clamped}
          onChange={onSliderChange}
          aria-label={`${name} Position`}
          className={`h-1.5 w-full appearance-none rounded-full accent-sky-500 ${
            styles.isDark ? 'bg-gray-700' : 'bg-gray-200'
          }`}
        />
      </div>
    </div>
  );
}
